import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { cn } from "utils/shadcn";
import {
  Tooltip as TooltipUI,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "app/components/ui/tooltip";

type Props = {
  watch?: (name: string) => any;
  content: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  contentClassName?: string;
};

export const Tooltip = ({
  watch,
  content,
  side = "top",
  contentClassName,
}: Props) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [hasTouch, setHasTouch] = useState(false);
  const touchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const location = useLocation();

  useEffect(() => {
    const checkTouchDevice = () => {
      setHasTouch("ontouchstart" in window || navigator.maxTouchPoints > 0);
    };

    checkTouchDevice();
    window.addEventListener("resize", checkTouchDevice);

    return () => window.removeEventListener("resize", checkTouchDevice);
  }, []);

  useEffect(() => {
    return () => {
      if (touchTimerRef.current) {
        clearTimeout(touchTimerRef.current);
      }
    };
  }, []);

  const handleTouchStart = () => {
    if (!hasTouch) return;

    if (touchTimerRef.current) {
      clearTimeout(touchTimerRef.current);
    }

    touchTimerRef.current = setTimeout(() => {
      setIsTooltipOpen(true);
    }, 500);
  };

  const handleTouchEnd = () => {
    if (!hasTouch) return;

    if (touchTimerRef.current) {
      clearTimeout(touchTimerRef.current);
      touchTimerRef.current = null;
    }
  };

  const isPlainTextContent =
    typeof content === "string" || typeof content === "number";

  const handleClick = (e: React.MouseEvent) => {
    if (hasTouch) {
      e.preventDefault();
      setIsTooltipOpen((prev) => !prev);
    } else {
      setIsTooltipOpen((prev) => !prev);
    }
  };

  return (
    <TooltipProvider>
      <TooltipUI
        open={isTooltipOpen}
        onOpenChange={setIsTooltipOpen}
        delayDuration={300}
      >
        <TooltipTrigger
          asChild
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onClick={handleClick}
        >
          <div className="shrink-0 inline-flex items-center cursor-pointer">
            {location.pathname === "/pricing" ? (
              <img
                src="/assets/help.svg"
                alt="help"
                className="shrink-0 size-4 sm:size-[1.125rem] lg:size-5"
                aria-hidden="true"
                draggable="false"
              />
            ) : location.pathname === "/about-esim" ||
              location.pathname === "/my-account/dashboard" ||
              location.pathname === "/my-account/sim-service" ? (
              "ⓘ"
            ) : location.pathname ===
              "/activation/fixed-phone/activation-code" ? (
              <div className="absolute right-0 top-1/2 -translate-y-1/2 size-[1.125rem] md:size-5">
                <img
                  src="/assets/question-mark.svg"
                  alt="Question Mark"
                  className="shrink-0 size-full"
                  aria-hidden="true"
                  draggable="false"
                />
              </div>
            ) : null}
            <span className="sr-only">Learn about our information</span>
          </div>
        </TooltipTrigger>
        <TooltipContent
          side={side}
          sideOffset={10}
          className={cn(
            "w-[calc(100vw-5rem)] bg-white p-2.5 sm:p-3 lg:p-3.5 rounded-xl sm:rounded-2xl ml-8 mr-4 shadow-[0_4px_45.8px_rgba(0,0,0,0.05)]",
            isPlainTextContent
              ? "max-w-[25.9375rem]"
              : "max-w-[min(92vw,22rem)]",
            contentClassName,
          )}
          onPointerDownOutside={(e) => {
            e.preventDefault();
            setIsTooltipOpen(false);
          }}
        >
          {isPlainTextContent ? (
            <p className="text-center text-[#3C3C3C] text-xs sm:text-[0.78125rem] lg:text-[0.8125rem] font-medium leading-[1.125rem] sm:leading-[1.1875rem] lg:leading-5">
              {content}
            </p>
          ) : (
            <div className="text-[#3C3C3C] text-xs sm:text-[0.78125rem] lg:text-[0.8125rem] font-medium leading-[1.125rem] sm:leading-[1.1875rem] lg:leading-5">
              {content}
            </div>
          )}
        </TooltipContent>
      </TooltipUI>
    </TooltipProvider>
  );
};
